import React, { useState, useEffect } from 'react';
import { getApiConfigs, getApiConfig, updateApiConfig, createApiConfig, getAllMerchants } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Search, Settings, Plus, Edit, Trash2, DollarSign, Percent, ArrowRight, Code, Security, List, Power, PowerOff, BarChart3, Activity, Users, Store } from 'lucide-react';

const ApiConfigsManagement = () => {
  const [apiConfigs, setApiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  
  const defaultFormData = {
    apiCode: '',
    apiName: '',
    apiEndpoint: '',
    pricingType: 'FIXED_CHARGE',
    fixedCharge: '',
    gstPercentage: '',
    commissionAmount: '',
    commissionType: 'FIXED',
    tdsPercentage: '',
    isActive: true,
    description: '',
    externalUsername: '',
    externalToken: '',
    dailyLimit: '',
    monthlyLimit: '',
    perTransactionLimit: ''
  };
  
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    loadApiConfigs();
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      const response = await getAllMerchants();
      setMerchants(response.data || []);
    } catch (error) {
      console.error('Failed to load merchants:', error);
    }
  };

  const loadApiConfigs = async () => {
    try {
      setLoading(true);
      const response = await getApiConfigs();
      setApiConfigs(response.data || []);
    } catch (error) {
      console.error('Failed to load API configs:', error);
      showErrorToast('Failed to load API configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditConfig = async (config) => {
    setSelectedConfig(config);
    setFormData({
      ...defaultFormData,
      ...config
    });
    // Load assigned merchants for editing
    try {
      const res = await getApiConfig(config.apiCode);
      if (res.data.external_username) {
        const merchantIds = res.data.external_username.split(',').filter(id => id.trim());
        setSelectedMerchants(merchantIds);
      } else {
        setSelectedMerchants([]);
      }
    } catch (error) {
      console.error('Failed to load assigned merchants:', error);
      setSelectedMerchants([]);
    }
    setEditDialogOpen(true);
  };

  const handleAddConfig = () => {
    setSelectedConfig(null);
    setFormData(defaultFormData);
    setSelectedMerchants([]);
    setEditDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Include merchant IDs in external_username field
      const configDataWithMerchants = {
        ...formData,
        external_username: selectedMerchants.length > 0 ? selectedMerchants.join(',') : null
      };
      
      if (selectedConfig) {
        // Update existing config
        await updateApiConfig(selectedConfig.apiCode, configDataWithMerchants);
        showSuccessToast('API configuration updated successfully');
      } else {
        // Create new config
        await createApiConfig(configDataWithMerchants);
        showSuccessToast('API configuration created successfully');
      }
      
      setEditDialogOpen(false);
      loadApiConfigs();
    } catch (error) {
      console.error('Failed to save config:', error);
      showErrorToast(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (config) => {
    try {
      await updateApiConfig(config.apiCode, { ...config, isActive: !config.isActive });
      showSuccessToast(`API ${config.isActive ? 'disabled' : 'enabled'} successfully`);
      loadApiConfigs();
    } catch (error) {
      showErrorToast('Failed to toggle API status');
    }
  };

  const filteredConfigs = apiConfigs.filter(config => 
    config.apiName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.apiCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPricingBadge = (type) => {
    const colors = {
      'FIXED_CHARGE': 'bg-blue-100 text-blue-800',
      'SLAB_BASED': 'bg-purple-100 text-purple-800',
      'COMMISSION': 'bg-green-100 text-green-800',
      'FIXED_ON_TRANSACTION': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colors[type] || 'bg-gray-100'}>{type?.replace(/_/g, ' ')}</Badge>;
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header with visual guide */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-600 rounded-lg p-2">
                <List className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">API Configs Management</h1>
                <p className="text-blue-700">Overview and quick management of all APIs</p>
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <div className="bg-white rounded-lg p-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">{apiConfigs.length} APIs</span>
              </div>
              <div className="bg-white rounded-lg p-3 flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium">{apiConfigs.filter(c => c.isActive).length} Active</span>
              </div>
            </div>
          </div>
          {/* <Button onClick={handleAddConfig} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Add New API
          </Button> */}
        </div>
        
        {/* Quick navigation hint */}
        <div className="mt-4 bg-blue-100 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>💡 Quick Tip:</strong> For detailed configuration (pricing, slabs, etc.), click "Configure" on any API or go to "API Config" in the sidebar
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Search className="h-5 w-5 text-gray-600" />
                Search & Filter APIs
              </h3>
              <p className="text-sm text-gray-600">Find APIs by name or code</p>
            </div>
            <Badge variant="outline" className="text-sm">
              {filteredConfigs.length} of {apiConfigs.length} results
            </Badge>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              className="pl-9" 
              placeholder="Search API configurations..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">API Details</TableHead>
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">Pricing Model</TableHead>
                <TableHead className="font-semibold">Rate</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="w-32 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredConfigs.map((config) => (
                <TableRow key={config.apiCode}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{config.apiName}</p>
                      <p className="text-xs text-gray-500">{config.apiEndpoint}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">{config.apiCode}</Badge>
                  </TableCell>
                  <TableCell>{getPricingBadge(config.pricingType)}</TableCell>
                  <TableCell>
                    {config.pricingType === 'FIXED_CHARGE' || config.pricingType === 'FIXED_ON_TRANSACTION' ? (
                      <span>₹{config.fixedCharge} + {config.gstPercentage}% GST</span>
                    ) : config.pricingType === 'COMMISSION' ? (
                      <span className="text-green-600">{config.commissionAmount}% - {config.tdsPercentage}% TDS</span>
                    ) : (
                      <span className="text-purple-600">Slab-based</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={config.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => handleEditConfig(config)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleToggleStatus(config)}
                        className={config.isActive ? 'text-red-500' : 'text-green-500'}
                      >
                        {config.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredConfigs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No API configurations found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedConfig ? 'Edit API Configuration' : 'Add API Configuration'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Settings</TabsTrigger>
                <TabsTrigger value="pricing">Pricing Configuration</TabsTrigger>
                <TabsTrigger value="limits">Limits & Security</TabsTrigger>
                <TabsTrigger value="merchants">Merchant Assignment</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>API Code *</Label>
                    <Input 
                      value={formData.apiCode}
                      onChange={(e) => handleInputChange('apiCode', e.target.value)}
                      placeholder="e.g., PAYOUT_API"
                      disabled={!!selectedConfig}
                      required
                    />
                  </div>
                  <div>
                    <Label>API Name *</Label>
                    <Input 
                      value={formData.apiName}
                      onChange={(e) => handleInputChange('apiName', e.target.value)}
                      placeholder="e.g., Payout Service"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>API Endpoint *</Label>
                  <Input 
                    value={formData.apiEndpoint}
                    onChange={(e) => handleInputChange('apiEndpoint', e.target.value)}
                    placeholder="https://api.example.com/payout"
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="API description and usage notes..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.isActive} 
                    onCheckedChange={(v) => handleInputChange('isActive', v)} 
                  />
                  <Label>Enable API</Label>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4 mt-4">
                <div>
                  <Label>Pricing Type</Label>
                  <Select value={formData.pricingType} onValueChange={(v) => handleInputChange('pricingType', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED_CHARGE">Fixed Charge</SelectItem>
                      <SelectItem value="FIXED_ON_TRANSACTION">Fixed on Transaction</SelectItem>
                      <SelectItem value="COMMISSION">Commission Based</SelectItem>
                      <SelectItem value="SLAB_BASED">Slab Based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.pricingType === 'FIXED_CHARGE' || formData.pricingType === 'FIXED_ON_TRANSACTION') && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fixed Charge (₹)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={formData.fixedCharge}
                        onChange={(e) => handleInputChange('fixedCharge', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>GST Percentage (%)</Label>
                      <Input 
                        type="number" 
                        step="0.1"
                        value={formData.gstPercentage}
                        onChange={(e) => handleInputChange('gstPercentage', e.target.value)}
                        placeholder="18"
                      />
                    </div>
                  </div>
                )}

                {formData.pricingType === 'COMMISSION' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Commission Amount</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={formData.commissionAmount}
                        onChange={(e) => handleInputChange('commissionAmount', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Commission Type</Label>
                      <Select value={formData.commissionType} onValueChange={(v) => handleInputChange('commissionType', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FIXED">Fixed (₹)</SelectItem>
                          <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>TDS Percentage (%)</Label>
                      <Input 
                        type="number" 
                        step="0.1"
                        value={formData.tdsPercentage}
                        onChange={(e) => handleInputChange('tdsPercentage', e.target.value)}
                        placeholder="5"
                      />
                    </div>
                  </div>
                )}

                {formData.pricingType === 'SLAB_BASED' && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800">
                      For slab-based pricing, configure charge slabs separately in the Slab Management section.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="limits" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Daily Limit (₹)</Label>
                    <Input 
                      type="number" 
                      placeholder="No limit" 
                      value={formData.dailyLimit}
                      onChange={(e) => handleInputChange('dailyLimit', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Monthly Limit (₹)</Label>
                    <Input 
                      type="number" 
                      placeholder="No limit" 
                      value={formData.monthlyLimit}
                      onChange={(e) => handleInputChange('monthlyLimit', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Per Transaction Limit (₹)</Label>
                    <Input 
                      type="number" 
                      placeholder="No limit" 
                      value={formData.perTransactionLimit}
                      onChange={(e) => handleInputChange('perTransactionLimit', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>External Username</Label>
                    <Input 
                      value={formData.externalUsername}
                      onChange={(e) => handleInputChange('externalUsername', e.target.value)}
                      placeholder="API username"
                    />
                  </div>
                  <div>
                    <Label>External Token</Label>
                    <Input 
                      type="password"
                      value={formData.externalToken}
                      onChange={(e) => handleInputChange('externalToken', e.target.value)}
                      placeholder="API token or key"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="merchants" className="space-y-4 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="mb-4">
                    <Label className="text-base font-medium flex items-center">
                      <Store className="h-4 w-4 mr-2" />
                      Assign to Merchants
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">Select which merchants can use this API</p>
                  </div>
                  
                  <div className="border rounded-lg p-3 max-h-60 overflow-y-auto bg-white">
                    {merchants.length > 0 ? (
                      merchants.map((merchant) => (
                        <div key={merchant.merchantId || merchant.id} className="flex items-center space-x-2 mb-2 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            id={`merchant-${merchant.merchantId || merchant.id}`}
                            checked={selectedMerchants.includes(merchant.merchantId || merchant.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMerchants([...selectedMerchants, merchant.merchantId || merchant.id]);
                              } else {
                                setSelectedMerchants(selectedMerchants.filter(id => id !== (merchant.merchantId || merchant.id)));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <Label htmlFor={`merchant-${merchant.merchantId || merchant.id}`} className="text-sm cursor-pointer flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">{merchant.merchantName || merchant.name || 'N/A'}</span>
                                <span className="text-gray-500 ml-2">({merchant.businessName || 'N/A'})</span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {merchant.status || 'ACTIVE'}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              ID: {merchant.merchantId || merchant.id} | Email: {merchant.email || 'N/A'}
                            </div>
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm font-medium">No merchants available</p>
                        <p className="text-xs">Please ensure merchants are active and verified</p>
                      </div>
                    )}
                  </div>
                  
                  {selectedMerchants.length > 0 && (
                    <div className="mt-3 p-2 bg-blue-100 rounded text-sm text-blue-700">
                      <strong>{selectedMerchants.length} merchant{selectedMerchants.length > 1 ? 's' : ''} selected</strong>
                      <p className="text-xs mt-1">These merchants will be able to use this API once it's active</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : (selectedConfig ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiConfigsManagement;
